import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from '../../hooks/useSearchParams';
import { isAiQueryType } from '../utils/searchUtils';

/**
 * Represents cached data from an AI search, including:
 * - listingIds: IDs of results from the AI search
 * - aiAnswer: The AI's explanation of the results
 */

/**
 * Hook for managing cached search data including listing IDs and AI answers.
 * Used to maintain state when transitioning from AI search to filtered/sorted results.
 */
export function useCachedSearchData() {
  const [cachedData, setCachedData] = useState({
    listingIds: []
  });
  const params = useSearchParams();
  const queryType = params.get('queryType');
  const searchTerm = params.get('search');

  // Reset cache when search term or query type changes
  useEffect(() => {
    if (isAiQueryType(queryType)) {
      setCachedData({
        listingIds: []
      });
    }
  }, [searchTerm, queryType]);

  // Update cache with new listing IDs and AI answer
  const updateCachedData = useCallback((listingIds, aiAnswer) => {
    setCachedData({
      listingIds,
      aiAnswer
    });
  }, []);

  // Use cache only for AI searches with cached results
  const shouldUseCache = useMemo(() => isAiQueryType(queryType) && cachedData.listingIds.length > 0, [queryType, cachedData.listingIds.length]);

  // Process search results to extract and cache listing IDs and AI answer
  const processSearchResult = useCallback(result => {
    const cards = result === null || result === void 0 ? void 0 : result.cards;
    const aiAnswer = result === null || result === void 0 ? void 0 : result.aiAnswer;
    if (isAiQueryType(queryType) && cards !== null && cards !== void 0 && cards.length && aiAnswer) {
      updateCachedData(cards.map(card => card.listingId), aiAnswer);
    }
  }, [queryType, updateCachedData]);

  // Get processed data, ensuring AI answer is preserved when using cached results
  const getProcessedData = useCallback(result => {
    if (!result) return null;
    return isAiQueryType(queryType) && cachedData.aiAnswer ? Object.assign({}, result, {
      aiAnswer: cachedData.aiAnswer
    }) : result;
  }, [queryType, cachedData.aiAnswer]);
  return {
    cachedAiAnswer: cachedData.aiAnswer,
    cachedListingIds: cachedData.listingIds,
    getProcessedSearchData: getProcessedData,
    processSearchResultData: processSearchResult,
    shouldUseCachedSearchData: shouldUseCache,
    updateCachedSearchData: updateCachedData
  };
}